import { Fragment } from "react";
import { Helmet } from "react-helmet-async";
import {
  HiOutlineBanknotes,
  HiOutlineClock,
  HiOutlineCalculator,
  HiOutlineArrowTrendingDown,
} from "react-icons/hi2";

import s from "./StaticPage.module.css";
import borrowing from "../../images/borrowing.jpg";
import { bankName, currencySymbol } from "../../util/config";

const Borrowing = () => {
  return (
    <Fragment>
      <Helmet>
        <title>Borrowing — {bankName}</title>
        <meta
          name="description"
          content={`Personal loans from ${currencySymbol}1,000 with fixed repayments and clear pricing from ${bankName}.`}
        />
        <link rel="canonical" href="/borrowing" />
      </Helmet>
      <section className={s.page}>
        <div
          className={s.hero}
          style={{ backgroundImage: `url(${borrowing})` }}
        >
          <div className={s.heroOverlay} />
          <div className={s.heroContent}>
            <p className={s.eyebrow}>Personal lending</p>
            <h1>Borrow with certainty—fixed payments, clear terms</h1>
            <p className={s.lead}>
              From {currencySymbol}1,000 to {currencySymbol}25,000 with predictable monthly
              instalments. Know your rate, your schedule, and your options to
              overpay—so you stay in control from day one.
            </p>
          </div>
        </div>

        <div className={s.metrics}>
          <div className={s.metric}>
            <span className={s.metricValue}>{currencySymbol}1k–{currencySymbol}25k</span>
            <span className={s.metricLabel}>Typical personal loan range</span>
          </div>
          <div className={s.metric}>
            <span className={s.metricValue}>Fixed</span>
            <span className={s.metricLabel}>Monthly repayments</span>
          </div>
          <div className={s.metric}>
            <span className={s.metricValue}>0%</span>
            <span className={s.metricLabel}>Overpayment fees on many plans</span>
          </div>
        </div>

        <div className={s.section}>
          <div className={s.sectionHeader}>
            <h2>Structured borrowing that fits real life</h2>
            <p className={s.sectionIntro}>
              Spread repayments from 1 to 5 years for loans up to {currencySymbol}15,000,
              or up to 8 years for larger amounts—so your instalments align
              with income and milestones, not guesswork.
            </p>
          </div>
          <div className={s.grid}>
            <div className={s.card}>
              <div className={s.cardIcon}>
                <HiOutlineCalculator aria-hidden />
              </div>
              <h3>Transparent pricing</h3>
              <p>
                See representative APR, total repayable, and monthly amounts
                before you sign. No hidden arrangement fees on standard
                products—what you approve is what we document.
              </p>
            </div>
            <div className={s.card}>
              <div className={s.cardIcon}>
                <HiOutlineClock aria-hidden />
              </div>
              <h3>Decisions that respect your time</h3>
              <p>
                Premier customers may receive an instant credit decision; others
                typically hear within two to five working days. We only proceed
                when affordability checks show borrowing is sustainable.
              </p>
            </div>
            <div className={s.card}>
              <div className={s.cardIcon}>
                <HiOutlineBanknotes aria-hidden />
              </div>
              <h3>Fast funding paths</h3>
              <p>
                Approved Premier customers may receive funds the same day after
                e-signing; other successful applications usually settle within
                three working days of receiving your signed agreement.
              </p>
            </div>
            <div className={s.card}>
              <div className={s.cardIcon}>
                <HiOutlineArrowTrendingDown aria-hidden />
              </div>
              <h3>Overpay when it suits you</h3>
              <p>
                Reduce interest by overpaying when you have surplus cash—many
                agreements allow this without penalty. Early settlement options
                are explained in your credit agreement.
              </p>
            </div>
          </div>
        </div>

        <div className={s.split}>
          <div className={s.splitText}>
            <h2>Credit that works with your broader finances</h2>
            <p>
              We assess income, existing commitments, and credit history using
              bureau data and internal models designed to prevent
              over-indebtedness. If we cannot offer a loan, we explain the
              principal reasons and signpost to money guidance where helpful.
            </p>
            <p>
              Missed payments can incur charges and harm your credit score. If
              you anticipate difficulty, contact us early—we have dedicated
              support pathways and may adjust arrangements within regulatory
              rules.
            </p>
          </div>
          <div className={s.splitText}>
            <div className={s.highlight}>
              <p>
                <strong>Premium touchpoints.</strong> Premier clients may access
                a lending specialist for larger requests, consolidation
                strategies, and coordination with your {bankName} current and
                savings accounts—always with suitability at the centre.
              </p>
            </div>
          </div>
        </div>

        <p className={s.trustStrip}>
          Loans are subject to status and affordability. Security may be
          required for some products. {bankName} is committed to responsible
          lending under FINMA rules; read your pre-contract credit information
          and agreement carefully before you borrow.
        </p>
      </section>
    </Fragment>
  );
};

export default Borrowing;
